//#region src/constants/projectFeatures.ts
const ProjectFeature = {
	SandboxesPythonSpark: "sandboxes-python-spark",
	GcpMarketplaceByodb: "gcp-marketplace-byodb",
	GcpMarketplaceByodbEmpty: "gcp-marketplace-byodb-empty",
	FrontendLogin: "frontend-login",
	ComponentsMcp: "ai-components-mcp",
	PayAsYouGo: "pay-as-you-go",
	ShowVault: "show-vault",
	DeprecatedGoogleDriveTranspose: "deprecated-google-drive-transpose",
	SandboxesPersistentVolume: "sandboxes-persistent-volume",
	WorkspaceSnowflakeDynamicBackendSize: "workspace-snowflake-dynamic-backend-size",
	DynamicBackendJobs: "dynamic-backend-jobs",
	HasSimplifiedComponents: "has-simplified-components",
	FlowMigration: "flow-migration",
	FlowMigrationBatch1: "flow-migration-batch1",
	FlowMigrationBatch2: "flow-migration-batch2",
	FlowMigrationBatch3: "flow-migration-batch3",
	OracleTransformations: "oracle-transformations",
	ExternalBuckets: "external-buckets",
	InputMappingReadOnlyStorage: "input-mapping-read-only-storage",
	Artifacts: "artifacts",
	SnowflakePartnerConnect: "snowflake-partner-connect",
	SnowflakePartnerConnectLimited: "snowflake-partner-connect-limited",
	NewNativeTypes: "new-native-types",
	DisableLegacyBucketPrefix: "disable-legacy-bucket-prefix",
	SoxProtectedDefaultBranch: "protected-default-branch",
	StorageBranches: "storage-branches",
	DataStreams: "data-streams",
	AiLineage: "ai-lineage",
	AiComponentSuggestion: "ai-component-suggestion",
	AgentChat: "agent-chat",
	AgentChatUseKaiAgentUrl: "agent-chat-use-kai-agent-url",
	AgentChatModelTiers: "agent-chat-model-tiers",
	BucketExposure: "bucket-exposure",
	WebMcp: "webmcp",
	SnowflakeRegisterExternalSecureDataShare: "snowflake-register-external-secure-data-share",
	DirectGrantOutputMapping: "direct-grant-output-mapping",
	EditorWithoutReadOnlyStorage: "editor-without-read-only-storage",
	BranchesMergeRequests: "branches-merge-requests",
	DenyExternalDatasetInputMapping: "deny-external-dataset-input-mapping",
	AllowSharedSnowflakeAccess: "allow-shared-snowflake-access",
	BigqueryDefaultImView: "bigquery-default-im-view"
};
//#endregion
//#region src/constants/serviceIds.ts
/**
* Every service a Keboola stack can announce in its `GET /v2/storage` index.
*
* - The values are the `id`s the stack reports; the service-client registry and `createStack`'s
*   lookups narrow on them. The index itself stays `string` — a stack may announce more.
* - Callers enumerate them at runtime — a test double announcing every service, for one.
*/
const ServiceId = {
	Vault: "vault",
	DataScience: "data-science",
	Encryption: "encryption",
	Telemetry: "telemetry",
	Ai: "ai",
	Editor: "editor",
	Import: "import",
	Oauth: "oauth",
	Query: "query",
	Queue: "queue",
	KaiAssistant: "kai-assistant",
	KaiAgent: "kai-agent",
	ComponentsMcp: "components-mcp",
	SyncActions: "sync-actions",
	Stream: "stream",
	Notification: "notification",
	Scheduler: "scheduler",
	Metastore: "metastore",
	Billing: "billing"
};
//#endregion
//#region src/constants/manageErrorCodes.ts
const ManageErrorCode = {
	ProgrammaticProjectInvalidClaim: "manage.programmaticProjects.invalidClaim",
	ProgrammaticProjectRateLimited: "manage.programmaticProjects.rateLimited",
	ProgrammaticProjectProvisioningFailed: "manage.programmaticProjects.provisioningFailed"
};
//#endregion
//#region src/constants/stackFeatures.ts
const StackFeature = {
	SingleTenant: "single-tenant",
	CanaryStack: "canary-stack",
	DisableJuliaAndR: "disable-julia-and-r",
	SamlRoles: "saml-roles",
	NewOauthService: "new-oauth-service-enabled",
	DataAppsEnforceAuth: "data-apps-enforce-auth",
	AllowAi: "allow-ai",
	UserWorkspaceDynamicBackendEnabled: "user-workspace-dynamic-backend-enabled",
	McpEnabled: "mcp-enabled",
	KaiEnabled: "kai-enabled",
	AgentProvisioning: "agent-provisioning",
	SandboxesPythonSpark: "sandboxes-python-spark"
};
//#endregion
//#region src/constants/adminFeatures.ts
const AdminFeature = {
	DisableAutosavingInFlows: "disable-autosaving-in-flows",
	CanUpdateProjectSettings: "can-update-project-settings",
	CanManageFeatures: "can-manage-features",
	CanManageUsers: "can-manage-users",
	CanManageDeletedProjects: "can-manage-deleted-projects",
	CanManageProjectLimits: "can-manage-project-limits",
	CanDisableProjects: "can-disable-projects",
	CanReactivateWorkspaces: "can-reactivate-workspaces",
	PowerUser: "power-user",
	MuteSoundNotifications: "mute-sound-notifications"
};
//#endregion
//#region src/constants/backends.ts
const Backend = {
	Snowflake: "snowflake",
	Bigquery: "bigquery"
};
//#endregion
//#region src/constants/stacks.ts
const Provider = {
	Aws: "aws",
	Azure: "azure",
	Gcp: "gcp"
};
const Continent = {
	Us: "us",
	Europe: "europe"
};
/**
* Every stack the platform knows, keyed by name.
*
* - The key set is literal, so a name that is not in the object is a compile error at the call site
*   rather than an `undefined` to fall back from.
* - The value type stays the uniform `KeboolaStack`, so `stack.isCanary` reads on any entry, not
*   only on the ones that carry the flag.
*/
const STACKS = {
	NORTH_EUROPE_AZURE: {
		id: "com-keboola-azure-north-europe",
		region: "north-europe",
		host: "connection.north-europe.azure.keboola.com",
		provider: "azure",
		continent: "europe",
		isPayg: true
	},
	EU_CENTRAL_1_AWS: {
		id: "kbc-eu-central-1",
		region: "eu-central-1",
		host: "connection.eu-central-1.keboola.com",
		provider: "aws",
		continent: "europe"
	},
	US_EAST_1_AWS: {
		id: "kbc-us-east-1",
		region: "us-east-1",
		host: "connection.keboola.com",
		provider: "aws",
		continent: "us"
	},
	EUROPE_WEST3_GCP: {
		id: "com-keboola-gcp-europe-west3",
		region: "europe-west3",
		host: "connection.europe-west3.gcp.keboola.com",
		provider: "gcp",
		continent: "europe"
	},
	US_EAST_4_GCP: {
		id: "com-keboola-gcp-us-east4",
		region: "us-east4",
		host: "connection.us-east4.gcp.keboola.com",
		provider: "gcp",
		continent: "us",
		isPayg: true
	},
	HCI: {
		id: "cloud-keboola-hci",
		host: "connection.hci.keboola.cloud",
		isSingleTenant: true
	},
	CSAS_PROD: {
		id: "cloud-keboola-cs",
		host: "connection.cs.keboola.cloud",
		isSingleTenant: true
	},
	CSAS_TEST: {
		id: "cloud-keboola-cs-test",
		host: "connection.cs-test.keboola.cloud",
		isSingleTenant: true
	},
	INNOGY: {
		id: "cloud-keboola-innogy-hub",
		host: "connection.innogy-hub.keboola.cloud",
		isSingleTenant: true
	},
	GROUPON: {
		id: "cloud-keboola-groupon",
		host: "connection.groupon.keboola.cloud",
		isSingleTenant: true
	},
	CREDIT_INFO: {
		id: "cloud-keboola-creditinfo",
		host: "connection.creditinfo.keboola.cloud",
		isSingleTenant: true
	},
	COATES: {
		id: "cloud-keboola-coates",
		host: "connection.coates.keboola.cloud",
		isSingleTenant: true
	},
	SLSP: {
		id: "cloud-keboola-slsp",
		host: "connection.slsp.keboola.cloud",
		isSingleTenant: true
	},
	HCKZ: {
		id: "cloud-keboola-hckz",
		host: "connection.hckz.keboola.cloud",
		isSingleTenant: true
	},
	PASHA: {
		id: "cloud-keboola-pasha",
		host: "connection.pasha.keboola.cloud",
		isSingleTenant: true
	},
	RBI: {
		id: "cloud-keboola-rbi",
		host: "connection.rbi.keboola.cloud",
		isSingleTenant: true
	},
	NAH: {
		id: "cloud-keboola-nah",
		host: "connection.nah.keboola.cloud",
		isSingleTenant: true,
		provider: "azure",
		continent: "us"
	},
	DEV_US_CENTRAL_1_GCP: {
		id: "dev-keboola-gcp-us-central1",
		region: "us-central1",
		host: "connection.us-central1.gcp.keboola.dev",
		provider: "gcp",
		continent: "us",
		isTesting: true
	},
	DEV_EU_WEST_1_AWS: {
		id: "dev-keboola-aws-eu-west-1",
		region: "eu-west-1",
		host: "connection.eu-west-1.aws.keboola.dev",
		provider: "aws",
		continent: "europe",
		isTesting: true
	},
	DEV_US_EAST_1_AWS_E2E: {
		id: "dev-keboola-aws-us-east-1-e2e",
		region: "us-east-1",
		host: "connection.e2e.us-east-1.aws.keboola.dev",
		provider: "aws",
		continent: "us",
		isTesting: true
	},
	DEV_US_EAST_2_AZURE: {
		id: "kbc-testing-azure-east-us-2",
		region: "east-us-2",
		host: "connection.east-us-2.azure.keboola-testing.com",
		provider: "azure",
		continent: "us",
		isTesting: true
	},
	DEV_KEBOOLA_CANARY_ORION: {
		id: "dev-keboola-canary-orion",
		region: "europe-west3",
		host: "connection.canary-orion.keboola.dev",
		provider: "gcp",
		continent: "europe",
		isCanary: true
	}
};
//#endregion
//#region src/constants/index.ts
const URLS = {
	USER_DOCUMENTATION: "https://help.keboola.com",
	COMPONENTS: "https://components.keboola.com",
	DEVELOPERS_DOCUMENTATION: "https://developers.keboola.com",
	ACADEMY: "https://academy.keboola.com",
	SALES_CONTACT_FORM: "https://keboola.com/contact",
	SNOWFLAKE: "https://www.snowflake.com",
	SNOWFLAKE_DOCS: "https://docs.snowflake.com",
	MCP: "https://www.keboola.com/mcp",
	CLI: "https://www.keboola.com/cli",
	NODE_JS: "https://nodejs.org/en",
	CLAUDE_CONNECTORS: "https://claude.ai/customize/connectors",
	CLIENT_CLAUDE_CODE_QUICKSTART: "https://code.claude.com/docs/en/quickstart",
	CLIENT_CLAUDE_AI: "https://claude.ai",
	CLIENT_CLAUDE_DESKTOP_DOWNLOAD: "https://claude.com/download",
	CLIENT_CURSOR_QUICKSTART: "https://cursor.com/docs/get-started/quickstart",
	CLIENT_VS_CODE_SETUP: "https://code.visualstudio.com/docs/setup/setup-overview",
	CLIENT_CODEX_QUICKSTART: "https://developers.openai.com/codex/cli",
	CLIENT_CHATGPT_DOWNLOAD: "https://chatgpt.com/download",
	CLIENT_MAKE_GET_STARTED: "https://help.make.com/get-started",
	CLIENT_DEVIN_DESKTOP: "https://docs.devin.ai/desktop/getting-started"
};
//#endregion
export { Backend as a, ManageErrorCode as c, STACKS as i, ServiceId as l, Continent as n, AdminFeature as o, Provider as r, StackFeature as s, URLS as t, ProjectFeature as u };

//# sourceMappingURL=constants-VpSwBtCb.js.map